import React, { useState } from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import { isEmpty, slice, filter, concat, chunk, orderBy } from 'lodash';
import { pipe } from 'lodash/fp';

import useMediaQuery from 'src/hooks/useMediaQuery';
import useCurrentCourse from '@src/hooks/useCurrentCourse';
import useRating from '@src/hooks/useRating';
import { formatToTimestamp } from '@src/utils/time';
import { IReview, IPropagandaReview } from '@src/types/course';

import Button from '@src/components/common/button';
import Header from '@src/components/common/header';
import ReviewItem from '@src/components/course/review/reviewItem';
import { BUTTON_VARIANT_OUTLINED } from '@src/const/common';

import AllReviewModal from './allReviewModal';
import { BlockWrapper } from '../styled';

const RATING_SHOW_LIMIT = 3;
const NUMBER_OF_DISPLAY = 6;

const ReviewsWrapper = styled(BlockWrapper)`
	display: flex;
	flex-direction: column;

	@media ${props => props.theme.device.tablet} {
		padding: 40px 28px 0;
	}

	@media ${props => props.theme.device.smTablet} {
		padding: 40px 28px 0;
	}

	@media ${props => props.theme.device.mobile} {
		padding: 40px 24px 0;
	}
`;

const ReviewContent = styled.div`
	display: grid;
	grid-template-columns: minmax(auto, 50%) minmax(auto, 50%);
	grid-column-gap: 24px;

	@media ${props => props.theme.device.smTablet} {
		display: block;
	}

	@media ${props => props.theme.device.mobile} {
		display: block;
	}
`;

const LeftBlock = styled.div`
	& > *:not(:last-child) {
		margin-bottom: 20px;
	}

	@media ${props => props.theme.device.tablet} {
		& > *:not(:last-child) {
			margin-bottom: 16px;
		}
	}
`;

const RightBlock = styled.div`
	margin-top: 24px;

	& > *:not(:last-child) {
		margin-bottom: 20px;
	}

	@media ${props => props.theme.device.tablet} {
		margin-top: 40px;

		& > *:not(:last-child) {
			margin-bottom: 16px;
		}
	}
`;

const ReviewItemWrapper = styled(ReviewItem)`
	position: relative;
	${props => (props.isLeft ? `
		&::before {
			content: '';
			position: absolute;
			bottom: 0;
			left: -16px;
			border-top: 40px solid transparent;
			border-right: 26px solid #F9F9F9;
		}
	` : `
		&::before {
			content: '';
			position: absolute;
			bottom: 0;
			right: -16px;
			border-top: 40px solid transparent;
			border-left: 26px solid #F9F9F9;
		}
	`)}

	@media ${props => props.theme.device.smTablet} {
		width: 444px;

		&:not(:last-child) {
			margin-bottom: 12px;
		}

		&:nth-child(odd){
			margin-left: 0;
			margin-right: auto;
		}

		&:nth-child(even){
			margin-left: auto;
			margin-right: 0;
		}
	}

	@media ${props => props.theme.device.mobile} {
		width: calc(100% - 20px);

		&:not(:last-child) {
			margin-bottom: 12px;
		}

		&:nth-child(odd){
			width: 100%;
		}

		&:nth-child(even){
			width: 100%;
		}
	}
`;

const ViewAllButton = styled(Button)`
	padding: 8px 24px;
	border: 1px solid #676767;
	border-radius: 8px;
	margin: 20px auto 0;
	color: #676767;
`;

const sortByIndex = arr => {
	const even = filter(arr, (review, index: number) => (index % 2) === 0);
	const odd = filter(arr, (review, index: number) => (index % 2) === 1);

	return concat(even, odd);
};

const Reviews = (): JSX.Element => {
	const { t } = useTranslation();
	const [isModalActive, setModalActive] = useState(false);
	const { mediaMatch } = useMediaQuery();
	const { currentCourse: { id } } = useCurrentCourse();
	const { courseRatings, propagandaReview } = useRating({ courseId: id, onlyMe: false });

	const reviewArr = pipe(
		// Format to timestamp to do sorting easily later.
		result => result.map(review => ({ ...review, updated_at: formatToTimestamp(review.updated_at) })),
		result => concat(result, courseRatings),
		result => orderBy(result, ['rating', 'updated_at'], ['desc', 'desc']),
		result => result.map(review => ({
			id: review.id,
			avatar: review.user?.profile_picture_link?.link || review.avatar_link?.link,
			userName: review.user?.name || review.student_name,
			updatedTime: review.updated_at,
			rating: review.rating,
			message: review.message,
			source: review.source,
			originalClassName: review.original_class_name,
		})),
	)(propagandaReview);

	const displayReview = pipe(
		result => filter(result, (review: IReview | IPropagandaReview): boolean => review.rating > RATING_SHOW_LIMIT),
		result => slice(result, 0, NUMBER_OF_DISPLAY),
	)(reviewArr);

	const sortedArr = pipe(
		sortByIndex,
		result => chunk(
			result,
			/**
			 * Should pile up based on number of review when its number is lower than `NUMBER_OF_DISPLAY`
			 * i.e [0, 2, 4, 1, 3] should be [[0, 2, 4], [1, 3]] after chunk.
			 */
			(result.length >= NUMBER_OF_DISPLAY) ? (NUMBER_OF_DISPLAY / 2) : Math.ceil(result.length / 2),
		),
	)(displayReview);

	const handleOpenModal = () => { setModalActive(true); };
	const handleCloseModal = () => { setModalActive(false); };

	return !isEmpty(displayReview) && (
		<ReviewsWrapper>
			<Header text={t('course:information:reviews')} />
			<ReviewContent>
				{(mediaMatch.isMobile || mediaMatch.isSmTablet) ? (
					displayReview.map((review, index) => (
						<ReviewItemWrapper
							key={review.id}
							isLeft={(index % 2) === 0}
							avatar={review.avatar}
							userName={review.userName}
							updatedTime={review.updatedTime}
							rating={review.rating}
							message={review.message}
							source={review.source}
							originalClassName={review.originalClassName}
							showPartial
						/>
					))
				) : (
					<>
						<LeftBlock>
							{sortedArr[0].map(review => (
								<ReviewItemWrapper
									key={review.id}
									isLeft
									avatar={review.avatar}
									userName={review.userName}
									updatedTime={review.updatedTime}
									rating={review.rating}
									message={review.message}
									source={review.source}
									originalClassName={review.originalClassName}
									showPartial
								/>
							))}
						</LeftBlock>
						{!isEmpty(sortedArr[1]) && (
							<RightBlock>
								{sortedArr[1].map(review => (
									<ReviewItemWrapper
										key={review.id}
										isLeft={false}
										avatar={review.avatar}
										userName={review.userName}
										updatedTime={review.updatedTime}
										rating={review.rating}
										message={review.message}
										source={review.source}
										originalClassName={review.originalClassName}
										showPartial
									/>
								))}
							</RightBlock>
						)}
					</>
				)}
			</ReviewContent>
			<ViewAllButton
				handleClick={handleOpenModal}
				variant={BUTTON_VARIANT_OUTLINED}
			>
				{t('course:view_all_review')}
			</ViewAllButton>
			{isModalActive && (
				<AllReviewModal
					reviewArr={reviewArr}
					isModalActive={isModalActive}
					handleClose={handleCloseModal}
				/>
			)}
		</ReviewsWrapper>
	);
};

export default Reviews;
